import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { logout } from '@/store/slices/authSlice';
import { addMediaToCartThunk, fetchMediaCartThunk } from '@/store/thunks/mediaThunk';
import type { MediaCartSummaryItem } from '@/types/media';

// UI state and cart mutations for the media feature. Read queries (books
// list, book detail) live in `src/store/api/mediaApi.ts` (RTK Query).
interface MediaState {
  search: string;
  activeType: string;
  cartCount: number;
  cartAdding: boolean;
  cartError: string | null;
  cartItems: MediaCartSummaryItem[];
  cartLoading: boolean;
}

const initialState: MediaState = {
  search: '',
  activeType: 'all',
  cartCount: 0,
  cartAdding: false,
  cartError: null,
  cartItems: [],
  cartLoading: false,
};

export const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    setMediaSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    setMediaType: (state, action: PayloadAction<string>) => {
      state.activeType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addMediaToCartThunk.pending, (state) => {
        state.cartAdding = true;
        state.cartError = null;
      })
      .addCase(addMediaToCartThunk.fulfilled, (state, action) => {
        state.cartAdding = false;
        state.cartCount += action.payload.data?.items?.length ?? 1;
      })
      .addCase(addMediaToCartThunk.rejected, (state, action) => {
        state.cartAdding = false;
        state.cartError = action.payload?.message ?? 'Unable to add item to cart.';
      })

      .addCase(fetchMediaCartThunk.pending, (state) => {
        state.cartLoading = true;
        state.cartError = null;
      })
      .addCase(fetchMediaCartThunk.fulfilled, (state, action) => {
        state.cartLoading = false;
        state.cartItems = action.payload.data.items;
        state.cartCount = action.payload.data.items.length;
      })
      .addCase(fetchMediaCartThunk.rejected, (state, action) => {
        state.cartLoading = false;
        state.cartError = action.payload?.message ?? 'Unable to load cart.';
      })

      .addCase(logout, () => initialState);
  },
});

export const { setMediaSearch, setMediaType } = mediaSlice.actions;
export const mediaReducer = mediaSlice.reducer;
